import {
  Combobox,
  type ComboboxRootProps,
  Field,
  type FieldRootProps,
  type ListCollection,
  Portal,
  TagsInput,
  createListCollection,
} from "@ark-ui/react";
import { X } from "@phosphor-icons/react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import {
  FormLabel,
  type FormLabelClassNames,
  type LabelOptionProps,
} from "../../shared/form-label/FormLabel.js";
import { BaseInput } from "../baseinput/BaseInput.js";
import { IconButton } from "../icon-button/IconButton.js";
import { Text } from "../text/Text.js";
import { useThemeContext } from "../theme/Theme.js";

export interface ComboboxItemObject {
  value: string | number;
  label: string;
  disabled?: boolean;
}
export type ComboboxDataProps = string[] | ComboboxItemObject[];

interface ComboboxProps extends Omit<ComboboxRootProps<any>, "collection"> {
  size?: "xs" | "sm" | "md" | "lg";
  label?: LabelOptionProps;
  disablePortal?: boolean;
  valuePlaceholder?: string;
  helperText?: HelperTextProps;
  items: ComboboxDataProps;
  wrapItemText?: boolean;
  error?: boolean;
  fieldProps?: FieldRootProps;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  nothingFoundText?: string;
  labelClassNames?: FormLabelClassNames;
  classNames?: {
    helperText?: string;
    clearTrigger?: string;
    trigger?: string;
    content?: string;
    item?: string;
    root?: string;
    actions?: string;
    label?: string;
    control?: string;
    itemText?: string;
    input?: string;
  };
}

const _Combobox = React.forwardRef<
  React.ElementRef<typeof Combobox.Input>,
  ComboboxProps
>((props, ref) => {
  const {
    items,
    classNames,
    label,
    openOnClick = true,
    multiple,
    inputBehavior = "autohighlight",
    size = "md",
    error,
    disablePortal = false,
    fieldProps,
    labelClassNames,
    wrapItemText = false,
    helperText = { message: "", status: "default" },
    nothingFoundText = "No results found",
    placeholder = "Search",
    ...rest
  } = props;

  const { primaryColor, secondaryColor } = useThemeContext();

  const [searchQuery, setSearchQuery] = React.useState("");

  const internalCollection: ListCollection<ComboboxItemObject> =
    React.useMemo(() => {
      return createListCollection({
        items: items.map((item): ComboboxItemObject => {
          if (typeof item === "string") {
            return {
              label: item,
              value: item,
            };
          } else {
            return {
              label: item.label,
              value: item.value,
              disabled: item.disabled,
            };
          }
        }),
      });
    }, [items]);

  const filteredItems = React.useMemo(() => {
    const data = internalCollection.items.filter((item) =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    return data;
  }, [internalCollection, searchQuery]);

  return (
    <Field.Root {...fieldProps} invalid={error}>
      <Combobox.Root
        className={clsx("ox-reset ox-ComboboxRoot", classNames?.root)}
        collection={internalCollection}
        inputBehavior={inputBehavior}
        openOnClick={openOnClick}
        multiple={multiple}
        placeholder={placeholder}
        onInputValueChange={(e) => setSearchQuery(e.inputValue)}
        {...rest}
      >
        {label ? (
          <FormLabel
            label={label}
            size={size}
            error={error}
            classNames={labelClassNames}
            component={Combobox.Label}
          />
        ) : null}

        <Combobox.Control
          className={clsx("ox-reset ox-ComboboxControl", classNames?.control)}
          data-size={size}
        >
          {multiple ? (
            <Combobox.Context>
              {(context) => (
                <>
                  <TagsInput.Root
                    data-size={size}
                    className={clsx("ox-reset ox-TagsInputRoot")}
                    value={context.value}
                  >
                    <TagsInput.Context>
                      {(tagsInput) => (
                        <>
                          <TagsInput.Control>
                            {tagsInput.value.map((value, index) => (
                              <TagsInput.Item
                                key={index}
                                index={index}
                                value={value}
                                data-size="sm"
                              >
                                <TagsInput.ItemPreview
                                  data-size="sm"
                                  className="ox-reset ox-ChipsItem"
                                >
                                  <TagsInput.ItemText>
                                    {value}
                                  </TagsInput.ItemText>
                                  <IconButton
                                    size="xs"
                                    variant="tertiary"
                                    color="gray"
                                    onClick={() => {
                                      context.clearValue(value);
                                    }}
                                    className="ox-reset ox-ChipsItemIcon"
                                  >
                                    <X
                                      size={16}
                                      fill="currentColor"
                                      className="ox-reset ox-ChipCloseIcon"
                                    />
                                  </IconButton>
                                </TagsInput.ItemPreview>
                                <TagsInput.ItemInput />
                              </TagsInput.Item>
                            ))}
                            <Combobox.Input asChild>
                              <BaseInput
                                ref={ref}
                                size={size}
                                unstyled
                                placeholder={placeholder}
                              />
                            </Combobox.Input>
                          </TagsInput.Control>
                        </>
                      )}
                    </TagsInput.Context>
                  </TagsInput.Root>
                </>
              )}
            </Combobox.Context>
          ) : (
            <>
              <Combobox.Input asChild>
                <BaseInput
                  ref={ref}
                  size={size}
                  error={error}
                  className={clsx(
                    "ox-ComboboxInput",
                    size === "sm"
                      ? "ox-fz-sm"
                      : size === "md"
                        ? "ox-fz-md"
                        : size === "lg"
                          ? "ox-fz-lg"
                          : "",
                    classNames?.input,
                  )}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Combobox.Input>
              <Combobox.ClearTrigger
                asChild
                className={clsx(
                  "ox-reset ox-ComboboxClearTrigger",
                  classNames?.clearTrigger,
                )}
              >
                <IconButton variant="tertiary" color="gray" size="xs">
                  <X />
                </IconButton>
              </Combobox.ClearTrigger>
            </>
          )}
        </Combobox.Control>

        <Portal disabled={disablePortal}>
          <Combobox.Positioner
            data-primary-color={primaryColor}
            data-secondary-color={secondaryColor}
          >
            <ScrollAreaPrimitive.Root
              type="hover"
              className="ox-reset ox-ScrollAreaRoot"
            >
              <Combobox.Content
                className={clsx(
                  "ox-reset ox-SelectContent",
                  classNames?.content,
                )}
                data-size={size}
                asChild
              >
                <ScrollAreaPrimitive.Viewport className="ox-reset ox-SelectContent">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item) => (
                      <Combobox.Item
                        data-size={size}
                        className={clsx(
                          "ox-reset ox-SelectItem",
                          classNames?.item,
                        )}
                        item={item}
                        key={item.value}
                      >
                        <Combobox.ItemText
                          data-wrapped={wrapItemText}
                          className={clsx(
                            "ox-reset ox-SelectItemText",
                            classNames?.itemText,
                          )}
                        >
                          {item.label}
                        </Combobox.ItemText>
                      </Combobox.Item>
                    ))
                  ) : (
                    <div
                      className="ox-reset ox-SelectItemNoResults"
                      data-size={size}
                    >
                      <Text
                        size={
                          size === "xs" || size === "sm"
                            ? "sm"
                            : size === "md"
                              ? "md"
                              : "lg"
                        }
                      >
                        {nothingFoundText}
                      </Text>
                    </div>
                  )}
                </ScrollAreaPrimitive.Viewport>
              </Combobox.Content>
              <ScrollAreaPrimitive.Scrollbar
                orientation="vertical"
                className="ox-reset ox-ScrollAreaScrollbar"
              >
                <ScrollAreaPrimitive.Thumb className="ox-reset ox-ScrollAreaThumb" />
              </ScrollAreaPrimitive.Scrollbar>
            </ScrollAreaPrimitive.Root>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>
      {helperText?.message ? (
        <Field.HelperText
          data-size={size}
          data-status={helperText?.status}
          className={clsx("ox-reset ox-FormHelperText", classNames?.helperText)}
        >
          {getFormHelperText(helperText, size)}
        </Field.HelperText>
      ) : null}
    </Field.Root>
  );
});

_Combobox.displayName = "Combobox";
export { _Combobox as Combobox };
export type { ComboboxProps };
